import { GoogleGenAI, Chat } from '@google/genai';
import { httpClient } from '../HttpClient';
import { TooManyRequestsError } from '../Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../Errors/ServiceUnavailableError';
import { NotValidJsonStringError } from '../Errors/NotValidJsonStringError';

export default class GeminiService {
    private genAi: GoogleGenAI;
    private model: string;
    private accumulatedInputTokens: number = 0;
    private accumulatedOutputTokens: number = 0;

    /**
     * Initializes the GeminiService with the provided API key.
     * @param {string} apiKey - The API key for Google GenAI.
     * @param {string} model - The model to use for generation.
     */
    
    constructor(model: string = '') {
        this.genAi = new GoogleGenAI({
            apiKey: 'proxy',
            httpOptions: {
                baseUrl: `${window.location.origin}/proxy/gemini`,
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                }
            }
        });
        this.model = model || process.env.MIX_GEMINI_TEXT_MODEL || '';
    }

    async generate(prompt: string, config: any = {}): Promise<string> {
        try {
            const response = await this.generateString(prompt, config);

            return JSON.parse(response);
        } catch (error: any) {
            if (error instanceof SyntaxError) throw new NotValidJsonStringError();
            
            throw error;
        }
    }

    async generateString(prompt: string, config: any = {}): Promise<string> {
        try {
            const response = await this.genAi.models.generateContent({
                model: this.model,
                contents: [{
                    parts: [{
                        text: prompt
                    }]
                }],
                config
            });

            this.accumulatedInputTokens += response.usageMetadata?.promptTokenCount ?? 0;
            this.accumulatedOutputTokens += response.usageMetadata?.candidatesTokenCount ?? 0;

            let generatedText = response.text?.trim() ?? '';

            generatedText = generatedText
                .replace(/^```json\s*/i, '')
                .replace(/^```\s*/i, '')
                .replace(/```$/, '')
                .trim();

            return generatedText;
        } catch (error: any) {
            const errorMessage = error.toString();
            const jsonMatch = errorMessage.match(/{.*}/);

            error = jsonMatch ? JSON.parse(jsonMatch[0]) : error;

            if (error.error.code === 429) throw new TooManyRequestsError();
            if (error.error.code === 503) throw new ServiceUnavailableError();

            throw error;
        }
    }

    async generateWithRetry(prompt: string, maxAttempts: number = 5): Promise<string> {
        let lastError: Error | null = null;
        
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await this.generate(prompt);
            } catch (err) {
                lastError = err as Error;
                const isRetryable =
                    err instanceof TooManyRequestsError ||
                    err instanceof ServiceUnavailableError ||
                    err instanceof NotValidJsonStringError;
                if (!isRetryable) {
                    throw err;
                }
                if (attempt < maxAttempts) {
                    await new Promise((res) => setTimeout(res, 5000));
                }
            }
        }
        
        throw lastError;
    }

    createChat(role: string = 'user', history: any = [{}]): Chat {
        return this.genAi.chats.create({
            model: this.model,
            history: [
                {
                    role: role,
                    parts: history
                },
                {
                    role: 'model',
                    parts: [{
                        text: 'Ok.'
                    }]
                }
            ]
        });
    }

    addTokens(inputCount: number, outputCount: number): void {
        this.accumulatedInputTokens += inputCount;
        this.accumulatedOutputTokens += outputCount;
    }

    async flushTokens(projectUuid: string, reason: string = 'Використання ШІ'): Promise<void> {
        if (this.accumulatedInputTokens === 0 && this.accumulatedOutputTokens === 0) return;

        const inputTokenAmount = this.accumulatedInputTokens;
        const outputTokenAmount = this.accumulatedOutputTokens;
        this.accumulatedInputTokens = 0;
        this.accumulatedOutputTokens = 0;

        await httpClient.post(`/projects/${projectUuid}/core/ai-transactions`, {
            input_token_amount: inputTokenAmount,
            output_token_amount: outputTokenAmount,
            reason,
            model: this.model
        });
    }
}